import styled from "styled-components";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";
import GenericHeader from "../../../Componentes/GenericHeader";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import { formatarData } from "../../../Funcoes/FormatarData";
import { useModal } from "../../../contexts/ModalContext";
import Overlay from "../../../Componentes/Overlay";
import SideMenu from "../../../Componentes/SideMenu";
import SideMenuBtn from "../../../Componentes/SideMenuBtn";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import ModalNovoDesvio from "./ModalNovoDesvio";
import AreasCatalogo from "./AreasCatalogo";
import ModalCatalogoCategRisco from "./ModalCatalogoCategRisco";
import ModalViewDesvio from "./ModalViewDesvio";
import ModalDeleteDesvio from "./ModalDeleteDesvio";
import useApi from "../../../hooks/useApi";

const PAGE_SIZE = 100;
const PERMISSAO_BASE = { modulo: "ssma", submodulo: "desvios" };

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const Statistics = styled.div`
    width: 350px;
    @media (max-width: 768px) {
        width: 100%;
        padding: 15px 10px;
    }
    height: 100%;
    background-color: #f3f3f3;
    border-radius: 8px;
    padding: 10px;
    box-sizing: border-box;
`;

const ContainerStatistic = styled.div`
    width: 100%;
    height: 100%;
    background-color: #f3f3f3;
    display: flex;
    flex-direction: column;
    gap: 3px;
    justify-content: center;
`;

const InfoContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
`;

const StatsInfoSide = styled.div`
    width: 45%;
`;

const TituloStatistic = styled.h3`
    text-align: center;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: bold;
    color: #000;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const Content = () => {
    const api = useApi();
    const { user } = useContext(AuthContext);
    const { openModal, isModalOpen, closeModal } = useModal();
    const navigate = useNavigate();

    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "apagar" }),
        [user]
    );

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [dataFiltered, setDataFiltered] = useState([]);
    const [desvioParaDelete, setDesvioParaDelete] = useState(null);

    const fetchData = async () => {
        setLoading(true);

        try {
            const { data } = await api.get("/Painel/API/Desvios/GetDesvios/", {
                withCredentials: true,
            });
            setData(data);
            setDataFiltered(data);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const [linhas, setLinhas] = useState(PAGE_SIZE);
    const tableRef = useRef(null);

    const loadMoreRows = useCallback(() => {
        setLinhas((prev) => {
            if (prev >= dataFiltered.length) return prev;
            return Math.min(prev + PAGE_SIZE, dataFiltered.length);
        });
    }, [dataFiltered.length]);

    useEffect(() => {
        setLinhas(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [dataFiltered]);

    const openNewDesvio = () => {
        closeModal();
        openModal("modalNovoDesvio");
    };

    const openCatalogoAreas = () => {
        closeModal();
        openModal("catalogoAreasDesvios");
    };

    const listaObras = useMemo(
        () => [...new Set(data.map((obj) => obj.centro_custo))].sort(),
        [data]
    ).filter((obra) => obra && obra.trim() !== "");
    const listaContratos = useMemo(
        () => [...new Set(data.map((obj) => obj.contrato))].sort(),
        [data]
    ).filter((ct) => ct && ct.trim() !== "");
    const listaResponsaveis = useMemo(
        () => [...new Set(data.map((obj) => obj.usuario))].sort(),
        [data]
    );
    const listaEquipes = useMemo(
        () => [...new Set(data.map((obj) => obj.equipe))].sort(),
        [data]
    );

    const openCatalogoCategRisco = () => {
        closeModal();
        openModal("catalogoCategRiscoDesvios");
    };

    const [filterColumns, setFilterColumns] = useState(4);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 800) {
                setFilterColumns(2);
            } else {
                setFilterColumns(4);
            }
        };

        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const [selectedObras, setSelectedObras] = useState([]);
    const [selectedContratos, setSelectedContratos] = useState([]);
    const [selectedResponsaveis, setSelectedResponsaveis] = useState([]);
    const [selectedEquipes, setSelectedEquipes] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedClassificacao, setSelectedClassificacao] = useState([]);

    const [dataInicio, setDataInicio] = useState(null);
    const [dataFim, setDataFim] = useState(null);

    useEffect(() => {
        let filtered = data;

        if (selectedObras.length > 0) {
            filtered = filtered.filter((item) => selectedObras.includes(item.centro_custo));
        }
        if (selectedContratos.length > 0) {
            filtered = filtered.filter((item) => selectedContratos.includes(item.contrato));
        }
        if (selectedResponsaveis.length > 0) {
            filtered = filtered.filter((item) => selectedResponsaveis.includes(item.usuario));
        }
        if (selectedEquipes.length > 0) {
            filtered = filtered.filter((item) => selectedEquipes.includes(item.equipe));
        }
        if (selectedStatus.length > 0) {
            filtered = filtered.filter((item) =>
                selectedStatus.includes(item.resolvido === 1 ? "FINALIZADO" : "PENDENTE")
            );
        }
        if (selectedClassificacao.length > 0) {
            filtered = filtered.filter((item) =>
                selectedClassificacao.includes(item.classificacao_risco)
            );
        }

        if (dataInicio) {
            const inicio = new Date(dataInicio);
            filtered = filtered.filter((item) => {
                const itemDate = new Date(item.data_ocorrido);
                return itemDate >= inicio;
            });
        }
        if (dataFim) {
            const fim = new Date(dataFim);
            filtered = filtered.filter((item) => {
                const itemDate = new Date(item.data_ocorrido);
                return itemDate <= fim;
            });
        }

        setDataFiltered(filtered);
    }, [
        selectedObras,
        selectedContratos,
        selectedResponsaveis,
        selectedEquipes,
        selectedStatus,
        selectedClassificacao,
        data,
        dataInicio,
        dataFim,
    ]);

    const [desvioSelected, setDesvioSelected] = useState(null);

    const openDesvio = (obj) => {
        setDesvioSelected(obj);
        openModal("modalViewDesvio");
    };

    const fecharViewDesvio = () => {
        setDesvioSelected(null);
        closeModal();
    };

    const abrirDelete = useCallback(
        (row) => {
            if (!podeApagar) return;
            setDesvioParaDelete(row);
            openModal("desvioDelete");
        },
        [podeApagar, openModal]
    );

    const fecharDelete = useCallback(() => {
        setDesvioParaDelete(null);
        closeModal();
    }, [closeModal]);

    const columns = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessor: "id",
                sortable: true,
                width: "70px",
            },
            {
                id: "centro_custo",
                header: "CENTRO CUSTO",
                accessor: "centro_custo",
                sortable: true,
            },
            {
                id: "contrato",
                header: "CONTRATO",
                accessor: "contrato",
                sortable: true,
            },
            {
                id: "area",
                header: "ÁREA",
                accessor: "area",
                sortable: true,
            },
            {
                id: "equipe",
                header: "EQUIPE",
                accessor: "equipe",
                sortable: true,
            },
            {
                id: "lider_imediato",
                header: "LÍDER",
                accessor: "lider_imediato",
                sortable: true,
            },
            {
                id: "usuario",
                header: "USUÁRIO",
                accessor: "usuario",
                sortable: true,
            },
            {
                id: "data_ocorrido",
                header: "DATA",
                accessor: (row) => (row.data_ocorrido ? formatarData(row.data_ocorrido) : "-"),
                sortable: true,
                sortValue: (row) => row.data_ocorrido,
            },
            {
                id: "classificacao_risco",
                header: "CLASSIFICAÇÃO",
                accessor: "classificacao_risco",
                sortable: true,
            },
            {
                id: "resolucao",
                header: "RESOLUÇÃO",
                accessor: (row) => (row.resolvido === 1 ? "FINALIZADO" : "PENDENTE"),
                sortable: true,
                sortValue: (row) => row.resolvido,
            },
            ...(podeApagar
                ? [
                      {
                          id: "acoes",
                          header: "AÇÕES",
                          sortable: false,
                          width: "70px",
                          align: "center",
                          render: (_, row) => (
                              <AcoesCelula>
                                  <BotaoAcao
                                      $danger
                                      title="Remover"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          abrirDelete(row);
                                      }}
                                  >
                                      <FiTrash2 size={14} />
                                  </BotaoAcao>
                              </AcoesCelula>
                          ),
                      },
                  ]
                : []),
        ],
        [podeApagar, abrirDelete]
    );

    return (
        <>
            <GenericHeader responsive>
                <Filters columns={filterColumns}>
                    <MultiselectCheck
                        options={listaObras.map((obra) => ({ value: obra, label: obra }))}
                        selectedOptions={selectedObras}
                        setSelectedOptions={setSelectedObras}
                        placeholder="CENTRO CUSTO..."
                    />
                    <MultiselectCheck
                        options={listaContratos.map((contrato) => ({
                            value: contrato,
                            label: contrato,
                        }))}
                        selectedOptions={selectedContratos}
                        setSelectedOptions={setSelectedContratos}
                        placeholder="CONTRATO..."
                    />
                    <MultiselectCheck
                        options={listaResponsaveis.map((resp) => ({ value: resp, label: resp }))}
                        selectedOptions={selectedResponsaveis}
                        setSelectedOptions={setSelectedResponsaveis}
                        placeholder="RESPONSÁVEL..."
                    />
                    <MultiselectCheck
                        options={listaEquipes.map((equipe) => ({ value: equipe, label: equipe }))}
                        selectedOptions={selectedEquipes}
                        setSelectedOptions={setSelectedEquipes}
                        placeholder="EQUIPE..."
                    />
                    <MultiselectCheck
                        options={[
                            { value: "FINALIZADO", label: "FINALIZADOS" },
                            { value: "PENDENTE", label: "PENDENTES" },
                        ]}
                        selectedOptions={selectedStatus}
                        setSelectedOptions={setSelectedStatus}
                        placeholder="STATUS..."
                    />
                    <MultiselectCheck
                        options={[
                            { value: "MODERADO", label: "MODERADO" },
                            { value: "ALTO", label: "ALTO" },
                        ]}
                        selectedOptions={selectedClassificacao}
                        setSelectedOptions={setSelectedClassificacao}
                        placeholder="CLASSIFICAÇÃO..."
                    />
                    <FilterContainer
                        type="date"
                        valor={dataInicio}
                        changeFunc={(value) => setDataInicio(value)}
                    />
                    <FilterContainer
                        type="date"
                        valor={dataFim}
                        changeFunc={(value) => setDataFim(value)}
                    />
                </Filters>
                <Statistics>
                    <ContainerStatistic>
                        <TituloStatistic>DESVIOS: {dataFiltered.length}</TituloStatistic>
                        <InfoContainer>
                            <StatsInfoSide>
                                <TextoStatistic>
                                    FINALIZADOS:{" "}
                                    {dataFiltered.filter((item) => item.resolvido === 1).length}
                                </TextoStatistic>
                                <TextoStatistic>
                                    PENDENTES:{" "}
                                    {dataFiltered.filter((item) => item.resolvido !== 1).length}
                                </TextoStatistic>
                            </StatsInfoSide>
                            <StatsInfoSide>
                                <TextoStatistic>
                                    MODERADOS:{" "}
                                    {
                                        dataFiltered.filter(
                                            (item) => item.classificacao_risco === "MODERADO"
                                        ).length
                                    }
                                </TextoStatistic>
                                <TextoStatistic>
                                    ALTOS:{" "}
                                    {
                                        dataFiltered.filter(
                                            (item) => item.classificacao_risco === "ALTO"
                                        ).length
                                    }
                                </TextoStatistic>
                            </StatsInfoSide>
                        </InfoContainer>
                    </ContainerStatistic>
                </Statistics>
            </GenericHeader>
            <TableFlexFill>
                <SortableTable
                    ref={tableRef}
                    columns={columns}
                    data={dataFiltered}
                    isLoading={loading}
                    loadingText="Carregando desvios..."
                    emptyText="NENHUM DESVIO ENCONTRADO"
                    initialSort={{ key: "id", direction: "desc" }}
                    getRowId={(row) => row.id}
                    onRowClick={openDesvio}
                    rowCursor="pointer"
                    limit={linhas}
                    onScrollReachEnd={loadMoreRows}
                />
            </TableFlexFill>
            {isModalOpen("sidebarDesvios") && (
                <Overlay state={true} changeContent={closeModal}>
                    <SideMenu>
                        <SideMenuBtn button="CATALOGO ÁREAS" clickFunc={openCatalogoAreas} />
                        <SideMenuBtn button="CATALOGO RISCOS" clickFunc={openCatalogoCategRisco} />
                        <SideMenuBtn
                            button="INVESTIGAÇÕES"
                            clickFunc={() => {
                                closeModal();
                                navigate("./investigacao");
                            }}
                        />
                        <SideMenuBtn
                            button="METAS"
                            clickFunc={() => {
                                closeModal();
                                navigate("./metas");
                            }}
                        />
                        <SideMenuBtn button="NOVO DESVIO" clickFunc={openNewDesvio} />
                    </SideMenu>
                </Overlay>
            )}
            {isModalOpen("modalNovoDesvio") && <ModalNovoDesvio onSave={fetchData} />}
            {isModalOpen("catalogoAreasDesvios") && <AreasCatalogo />}
            {isModalOpen("catalogoCategRiscoDesvios") && <ModalCatalogoCategRisco />}
            {isModalOpen("modalViewDesvio") && (
                <ModalViewDesvio
                    desvio={desvioSelected}
                    fechar={fecharViewDesvio}
                    updateData={fetchData}
                />
            )}
            {podeApagar && isModalOpen("desvioDelete") && desvioParaDelete != null && (
                <ModalDeleteDesvio
                    desvio={desvioParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={fetchData}
                />
            )}
        </>
    );
};

export default Content;
